import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";

import Container from "@mui/material/Container";
import ContentBrowser from "./browse/ContentBrowser";
import RequireRoles from "../../auth/RequireRoles";
import { useEditScreenClose } from "../use-editScreenClose";
import { Route, Routes, useLocation, useMatch } from "react-router-dom";
import {
  LOCAL_STORAGE_CONTENT_SEARCH_PREFERENCES_KEY,
  useSearch,
} from "../searchServices";
import { lensProp, mergeDeepRight, set } from "ramda";
import ContentEdit from "./edit/ContentEdit";
import { useLocalStorage } from "../../common/use-localStorage";
import { organizeRoles, useIsIapReviewer } from "../../auth/authUtils";
import { useNavigate } from "react-router-dom";

const useStyles = makeStyles(theme => ({
  panel: {
    display: "flex",
    flexDirection: "column",
    height: "95%",
    padding: "0",
    border: "3px solid white",
    background: "white",
    borderRadius: "20px",
  },
}));

export const initialStatus = {
  name: "status",
  queryParam: "status",
  terms: [
    { code: "(-status:* AND *:*)" }, // NULL status
    { code: "APPROVED" },
    { code: "PENDING" },
    { code: "REJECTED" },
    { code: "TAKEDOWN" },
  ],
};

const initialState = (initialState = {}, initialSearch) => {
  return set(
    lensProp("appliedQuery"),
    initialSearch,
    mergeDeepRight(
      {
        appliedQuery: [],
        appliedFacets: [
          {
            name: "type",
            queryParam: "_type",
            terms: [
              {
                code: "CONTENT",
              },
            ],
          },
          initialStatus,
        ],
        orderBy: "",
        orderDirection: "",
        page: 0,
        limit: 10,
      },
      initialState,
    ),
  );
};

const initialSearchState = {
  noFacets: true,
};

const initialFacetState = {
  noFacets: false,
  facetsOnly: true,
};

const ContentView = () => {
  const classes = useStyles();
  const isExact = useMatch("/creator/content") != null;
  const navigate = useNavigate();
  const location = useLocation();
  const isIapReviewer = useIsIapReviewer();

  useEffect(() => {
    // send IAP reviewers back to Workbench
    if (isExact && isIapReviewer) {
      navigate("/creator/organize?view=review,pending", { replace: true });
    }
  }, [navigate, isExact, isIapReviewer]);

  const [searchPreferences, setSearchPreferences] = useLocalStorage(
    LOCAL_STORAGE_CONTENT_SEARCH_PREFERENCES_KEY,
    {
      titleOnly: false,
    },
  );

  const initialSearchValue =
    location?.state?.search?.content?.appliedQuery ?? [];

  const [searchState, setSearchState] = useState(
    initialState(
      { ...initialSearchState, ...searchPreferences },
      initialSearchValue,
    ),
  );
  const [facetState, setFacetState] = useState(
    initialState(
      { ...initialFacetState, ...searchPreferences },
      initialSearchValue,
    ),
  );

  const searchResults = useSearch(searchState);
  const facetResults = useSearch(facetState);

  const handleEditScreenClose = useEditScreenClose("/content");

  const handleSave = useCallback(() => {
    handleEditScreenClose();
    searchResults.refetch();
    console.log("location from content view: ", location);
  }, [searchResults, handleEditScreenClose, location]);

  const handleCancel = useCallback(() => {
    handleEditScreenClose();
  }, [handleEditScreenClose]);

  return (
    <Container
      id="content-view"
      disableGutters
      maxWidth={false}
      classes={{ root: classes.panel }}
    >
      <Routes>
        <Route
          index
          element={
            <RequireRoles roles={["ROLE_ADMIN"]} showUnauthorized={false}>
              <ContentBrowser
                searchState={searchState}
                facetState={facetState}
                onSearchStateChange={state => setSearchState(state)}
                onFacetStateChange={state => setFacetState(state)}
                searchResults={searchResults}
                facetResults={facetResults}
                onSaveSuccess={handleSave}
                setSearchPreferences={setSearchPreferences}
              />
            </RequireRoles>
          }
        />
        <Route
          path=":id/*"
          element={
            <RequireRoles roles={organizeRoles} showUnauthorized={false}>
              <ContentEdit onCancel={handleCancel} onSaveSuccess={handleSave} />
            </RequireRoles>
          }
        />
      </Routes>
    </Container>
  );
};

export default ContentView;
